import QRCodeScanner from "@/components/QRCodeScanner";
import Title from "@/components/Title";
import {
  REGISTRATION_METHOD_SCAN,
  postDeviceIdRegistration,
  useRingIdRegistrationAPI,
} from "@/components/activation/DeviceIdRegistrationAPI";
import { deviceIdValidator } from "@/components/activation/DeviceIdService";
import {
  useNavigateRegisterRingDeviceIdOrCompleted,
  validateRingId,
} from "@/components/activation/device_id_registrations";
import { getEncodedCurrentPath } from "@/components/common/LocationService";
import { NonFieldError } from "@/components/common/SCAlert";
import { ScrollToTop } from "@/components/common/ScrollToTop";
import { ConfirmationDialog } from "@/components/examinee/ConfirmationDialog";
import { GeneralError } from "@/components/sleep_checkup_v1/GeneralError";
import { examineeTheme } from "@/theme/themeV1";
import { Close } from "@mui/icons-material";
import TaskAlt from "@mui/icons-material/TaskAlt";
import {
  Box,
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Stack,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// このページが受け取るstateの型
export type StateType = {
  sleepCheckupUuid: string;
};

type ScanState = "scanning" | "success" | "cancelling";

class PageState {
  scanState: ScanState;
  guideMessage: string[];
  deviceId: string | null;

  constructor(
    scanState: ScanState,
    guideMessage: string[] = [],
    deviceId: string | null = null
  ) {
    this.scanState = scanState;
    this.guideMessage = guideMessage;
    this.deviceId = deviceId;
  }
}

const GUIDE_MESSAGE_01 = ["デバイスのQRコードを", "読み取ってください。"];
const GUIDE_MESSAGE_02 = ["デバイスから10cm程度", "離して撮影しましょう。"];

const DEVICE_TYPE_LIST = ["ring", "watch"] as const;
type DeviceType = (typeof DEVICE_TYPE_LIST)[number];

export function DeviceIdScan({
  deviceType = "watch",
}: {
  deviceType?: DeviceType;
}) {
  const [pageState, setPageState] = useState<PageState>(
    new PageState("scanning", GUIDE_MESSAGE_01)
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const navigate = useNavigate();
  const { post: postRingIdRegistration } = useRingIdRegistrationAPI();
  const navigateRegisterRingDeviceIdOrCompleted =
    useNavigateRegisterRingDeviceIdOrCompleted();
  const location = useLocation();

  const { sleepCheckupUuid } = (location.state as StateType) || {};

  const onChancelHandler = useCallback(() => {
    if (deviceType === "watch") {
      return navigate("/examinee/device_id_input", {
        state: { sleepCheckupUuid: sleepCheckupUuid },
      });
    }
    return navigate("/examinee/ring_id_input", {
      state: { sleepCheckupUuid: sleepCheckupUuid },
    });
  }, [navigate, sleepCheckupUuid, deviceType]);

  const onAbortHandler = useCallback(() => {
    if (deviceType === "watch") {
      return navigate(
        `/examinee/device_id_scan_introduction/${sleepCheckupUuid}`
      );
    }
    return navigate(`/examinee/ring_id_scan_introduction/${sleepCheckupUuid}`);
  }, [navigate, deviceType, sleepCheckupUuid]);

  const onDetectHandler = useCallback(
    (data: string) => {
      if (deviceType === "watch") {
        // 時計型デバイスの場合
        if (deviceIdValidator(data) != null) {
          // デバイス以外のQRコードが読み取られたら無視する
          return;
        }
      } else {
        // 指輪型デバイスの場合
        if (validateRingId(data) != null) {
          // デバイス以外のQRコードが読み取られたら無視する
          return;
        }
      }

      setPageState(
        (state) => new PageState("success", state.guideMessage, data)
      );
    },
    [deviceType]
  );

  const onRegisterDeviceIdHandler = () => {
    if (pageState.deviceId == null) {
      return;
    }

    if (deviceType === "watch") {
      return postDeviceIdRegistration(
        pageState.deviceId,
        REGISTRATION_METHOD_SCAN,
        sleepCheckupUuid,
        getEncodedCurrentPath(location)
      )
        .then(() => navigateRegisterRingDeviceIdOrCompleted(sleepCheckupUuid))
        .catch((e) => {
          setPageState(
            (state) => new PageState("scanning", state.guideMessage)
          );
          setErrorMessage(
            `エラーが発生しました。しばらくしてから再度お試しいただくか、[読み取り中止] を押して [IDを手入力する] から手動でIDをご登録ください。\n(${e.message})`
          );
        });
    }
    return postRingIdRegistration({
      ringId: pageState.deviceId,
      registrationMethod: REGISTRATION_METHOD_SCAN,
      sleepCheckupUuid,
    })
      .then(() =>
        navigate("/examinee/registration_completed", {
          state: {
            sleepCheckupUuid: sleepCheckupUuid,
          },
        })
      )
      .catch((e) => {
        setPageState((state) => new PageState("scanning", state.guideMessage));
        setErrorMessage(
          `エラーが発生しました。しばらくしてから再度お試しいただくか、[読み取り中止] を押して [IDを手入力する] から手動でIDをご登録ください。\n(${e.message})`
        );
      });
  };

  const onTimeHandler = useCallback((round: number) => {
    // ガイドメッセージを4回切り替え（約1分経過）ても読み取れていなかったら、ポップアップを表示する
    if (round >= 5) {
      setPageState((state) => new PageState("cancelling", state.guideMessage));
    } else {
      setPageState((state) => {
        const message =
          state.guideMessage === GUIDE_MESSAGE_01
            ? GUIDE_MESSAGE_02
            : GUIDE_MESSAGE_01;
        return new PageState("scanning", message);
      });
    }
  }, []);

  const onResumeHandler = () => {
    setPageState((state) => new PageState("scanning", state.guideMessage));
  };

  const onErrorHandler = useCallback((e: any) => {
    setErrorMessage(
      `エラーが発生しました。しばらくしてから再度お試しいただくか、[読み取り中止] を押して [IDを手入力する] から手動でIDをご登録ください。\n(${e.message})`
    );
  }, []);

  if (!sleepCheckupUuid) {
    return (
      <GeneralError
        to="/examinee/signup"
        buttonText="アカウント登録をやり直す"
      />
    );
  }

  return (
    <ThemeProvider theme={examineeTheme}>
      <>
        <Title title="SLEEP COMPASS" />
        <meta
          name="description"
          content="専用デバイスによる、自宅でできる8日間の睡眠測定。高精度のアルゴリズムで眠れない理由を明らかにします。"
        />
      </>
      <ScrollToTop />
      <Box
        component="div"
        sx={{
          width: window.innerWidth,
          height: window.innerHeight,
          backgroundColor: "black",
          overflow: "hidden",
        }}
      >
        {errorMessage && <NonFieldError>{errorMessage}</NonFieldError>}
        {errorMessage == null && (
          <>
            {/* Note: 理論上、5秒毎にガイドメッセージが変わるように、scanTimeに5を設定している。が、実際は、端末のスペックなどによって描画に時間がかかったりするため、メッセージが切り替わる時間にはバラつきがあるので注意 */}
            <QRCodeScanner
              scan={pageState.scanState === "scanning"}
              width={window.innerWidth}
              height={window.innerHeight}
              scanTime={5}
              onDetect={onDetectHandler}
              onTime={onTimeHandler}
              onError={onErrorHandler}
            />
            <Stack
              sx={{ width: "100%", height: "100%" }}
              position="absolute"
              top={0}
              left={0}
              alignItems="center"
              justifyContent="center"
            >
              <ScanGuide messages={pageState.guideMessage} />
            </Stack>
          </>
        )}
        <Stack
          alignItems="center"
          position="absolute"
          bottom={24}
          sx={{ width: "100%" }}
        >
          <AbortButton onClick={() => onAbortHandler()} />
        </Stack>
      </Box>
      <ScanCancelDialog
        open={pageState.scanState === "cancelling"}
        onClick={() => onChancelHandler()}
        onClickClose={onResumeHandler}
      />
      <ScanSuccessfulDialog
        deviceId={pageState.deviceId}
        onClick={onRegisterDeviceIdHandler}
        onClickClose={onResumeHandler}
      />
    </ThemeProvider>
  );
}

type ScanGuideProps = GuideMessageProps;
function ScanGuide({ messages }: ScanGuideProps) {
  return (
    <Stack spacing={4} sx={{ pb: "70px" }}>
      <GuideMessage messages={messages} />
      <ScanFrame />
    </Stack>
  );
}

type GuideMessageProps = {
  messages: string[];
};
function GuideMessage({ messages }: GuideMessageProps) {
  return (
    <Stack>
      {messages.map((m, i) => (
        <Typography
          key={i}
          variant="subtitle2"
          color="white"
          textAlign="center"
        >
          {m}
        </Typography>
      ))}
    </Stack>
  );
}

function ScanFrame() {
  return (
    <img
      src="/img/image_scan_frame.svg"
      alt="QRコードの読み取り"
      loading="lazy"
      style={{ width: "200px", height: "auto" }}
    />
  );
}

type AbortButtonProps = {
  onClick: ButtonProps["onClick"];
};
function AbortButton({ onClick }: AbortButtonProps) {
  return (
    <Button
      variant="contained"
      size="medium"
      color="blackButton"
      sx={{
        borderRadius: "32px",
      }}
      startIcon={<Close />}
      onClick={onClick}
    >
      読み取り中止
    </Button>
  );
}

type ScanCancelDialogProps = {
  open: DialogProps["open"];
  onClick: ButtonProps["onClick"];
  onClickClose: () => void;
};
function ScanCancelDialog({
  open,
  onClick,
  onClickClose,
}: ScanCancelDialogProps) {
  return (
    <ConfirmationDialog
      open={open}
      title="お困りですか？"
      message="QRコードがうまく読み取れない場合、手入力によってデバイスIDを登録できます。"
      action={{ title: "手入力する", onClick: onClick }}
      onClickClose={onClickClose}
    />
  );
}

type ScanSuccessfulDialogProps = {
  deviceId: string | null;
  onClick: ButtonProps["onClick"];
  onClickClose: () => void;
};
function ScanSuccessfulDialog({
  deviceId,
  onClick,
  onClickClose,
}: ScanSuccessfulDialogProps) {
  const BUTTON_SX = { px: 4 };

  return (
    <Dialog
      open={deviceId != null}
      maxWidth="md"
      onClose={onClickClose}
      PaperProps={{ sx: { m: 4 } }}
    >
      <DialogTitle
        component={Stack}
        direction="row"
        justifyContent="center"
        alignItems="center"
        gap={2}
      >
        <TaskAlt />
        <Typography
          variant="h6"
          color="primary.main"
          sx={{
            fontFamily: "Noto Sans JP",
            fontWeight: 500,
            fontStyle: "Medium",
            fontSize: "20px",
            leadingTrim: "NONE",
            lineHeight: "170%",
            letterSpacing: "0.15px",
            textAlign: "center",
          }}
        >
          読み取り成功
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 6, mt: 4 }}>
        <Stack gap={2}>
          <Typography variant="body1">
            以下のデバイスIDが見つかりました。
          </Typography>
          <Typography variant="body2" color="primary">
            {deviceId}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 6 }}>
        <Stack
          direction="row"
          spacing={4}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Button
            sx={BUTTON_SX}
            variant="outlined"
            size="large"
            onClick={onClickClose}
            fullWidth
          >
            キャンセル
          </Button>
          <Button
            sx={BUTTON_SX}
            variant="contained"
            size="large"
            onClick={onClick}
            fullWidth
            disableElevation
          >
            登録する
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
